import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import ArrowRight from '@mui/icons-material/KeyboardArrowRight'
import IconButton from '@mui/material/IconButton'
import { useDimensions } from 'hooks/useDimensions'
import * as React from 'react'

export const CARD_LIST_HEIGHT_SMALL = 148
export const CARD_LIST_HEIGHT_DEFAULT = 240
export const ITEM_WIDTH_DEFAULT = 240
export const ITEM_WIDTH_SMALL = 144
export const CARD_LIST_HEIGHT_BIG = 272
const SPACING_VERTICAL_DEFAULT = 1.25
const SCROLL_BY_CONTAINER_RATIO = 0.75 // scroll step, relative to the container size (75%)

export interface CardListProps {
  children?: React.ReactNode[]
  itemWidth?: number | 'auto'
  listHeight?: number
  containerClassName?: string
  listClassName?: string
  disabled?: boolean
  skipCardHoverCheck?: boolean
  scrollBy?: number
  verticalSpacing?: number
  listItemClassName?: string

  onScrolled?(direction: 'left' | 'right'): void
  onScrollLimit?(): void
}

import { useElementSize } from 'hooks/useElementSize'
import styles from './CardList.pcss'

export function CardList({
  children,
  itemWidth = ITEM_WIDTH_DEFAULT,
  listHeight = CARD_LIST_HEIGHT_DEFAULT,
  containerClassName,
  listClassName,
  disabled,
  skipCardHoverCheck = true,
  scrollBy,
  verticalSpacing,
  listItemClassName,
  onScrolled,
  onScrollLimit
}: CardListProps) {
  const [xPosition, _setXPosition] = React.useState(0)
  const [containerRef, containerRect] = useDimensions([React.Children.count(children)], true)
  const [listRef, listRect] = useElementSize()

  const onIntersection = React.useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(e => {
      if (e.boundingClientRect.height > e.intersectionRect.height) {
        // vertical intersection, skipping
        return
      }
      e.target.classList.toggle(styles['no-hover'], e.isIntersecting && e.intersectionRatio < 1)
    })
  }, [])

  let scrollStep: number = scrollBy || 0
  if (scrollStep === 0) {
    scrollStep = itemWidth !== 'auto'
      ? containerRect.width - itemWidth
      : containerRect.width * SCROLL_BY_CONTAINER_RATIO
  }

  const vertSpacing = typeof verticalSpacing === 'undefined' ? SPACING_VERTICAL_DEFAULT : verticalSpacing
  const maxXPosition = Math.max(listRect.width - containerRect.width, 0) // Cannot be negative value
  const xRef = React.useRef(xPosition)

  const setXPosition = (pos: number) => {
    _setXPosition(pos)
    xRef.current = pos
  }

  const valueWithinRange = React.useCallback((value: number) => {
    if (value < 0) {
      return 0
    }
    if (value > maxXPosition) {
      return maxXPosition
    }
    return value
  }, [maxXPosition])

  // EXPL: maxXPosition can change on window resize. Force current x position to be within the valid range
  React.useEffect(() => {
    const position = valueWithinRange(xRef.current)
    if (xRef.current !== position) {
      setXPosition(position)
    }
  }, [maxXPosition, valueWithinRange])

  // EXPL: Watch children for visibility changes. If a child element is intersecting with container edges,
  // we want to make it "no-hoverable" using the no-hover css class
  React.useEffect(() => {
    if (skipCardHoverCheck || !listRef.current) {
      return
    }

    const observer = new IntersectionObserver(onIntersection, { root: containerRef.current, threshold: [0, 1] })

    for (let i = 0; i < listRef.current.children.length; i++) {
      const element = listRef.current.children.item(i)
      if (element) {
        observer.observe(element)
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [children?.length, skipCardHoverCheck, onIntersection, containerRef, listRef])

  const scrollLeft = () => {
    const next = valueWithinRange(xPosition - scrollStep)
    setXPosition(next)

    if (onScrolled) {
      onScrolled('left')
    }
  }

  const scrollRight = () => {
    const next = valueWithinRange(xPosition + scrollStep)
    setXPosition(next)

    if (next >= maxXPosition && onScrollLimit) {
      onScrollLimit()
    }
    if (onScrolled) {
      onScrolled('right')
    }
  }

  const buttonStyles = {
    height: `${listHeight}px`
  }

  const listStyles = {
    height: `${listHeight}px`,
    transform: `translateX(${-xPosition}px)`
  }

  const containerStyles = {
    height: `${listHeight * vertSpacing}px`
  }

  return (
    (<div
      ref={containerRef as any}
      style={containerStyles}
      className={`${styles.container} ${containerClassName || ''}`}
      data-testid="card-list"
    >
      <IconButton
        style={buttonStyles}
        className={`${styles['scroll-left']} ${xPosition === 0 ? styles.hide : ''}`}
        disableRipple
        disabled={disabled}
        onClick={scrollLeft}
        size="large"
      >
        <ArrowLeft className={styles['arrow-icon']} />
      </IconButton>
      <IconButton
        style={buttonStyles}
        className={`${styles['scroll-right']} ${xPosition === maxXPosition ? styles.hide : ''}`}
        disableRipple
        disabled={disabled}
        onClick={scrollRight}
        size="large"
      >
        <ArrowRight className={styles['arrow-icon']} />
      </IconButton>
      <ul
        ref={listRef as any}
        style={listStyles}
        className={`${styles.list} ${listClassName || ''}`}
      >
        {
          React.Children.toArray(children).map((child: React.ReactElement<any>, index: number) => {
            return (
              <li className={`${styles.item} ${listItemClassName || ''}`} key={child.key || index}>
                {child}
              </li>
            )
          })
        }
      </ul>
    </div>)
  )
}

export default CardList
